// © Microsoft Corporation. All rights reserved.

import {
    KeyValuePair,
    appEnvironment,
    headData,
    oneServiceQueryStringKeys
} from "@msnews/core";
import { OneServiceUtility, getServiceUrlForSubpath, sendRequest } from "@msnews/oneservice";

import { ContentActionsToolbarAppErrors } from "@msnews/diagnostics";
import { DocumentData } from "../ContentActionsToolbar.state";
import { Status } from "../ContentActionsToolbar.constants";
import { Telemetry } from "@msnews/telemetry-contracts";
import { get } from "lodash-es";

/**
 * Class to fetch data for the content actions toolbar experience
 * @class ContentActionsToolbarServiceClient
 */
export class ContentActionsToolbarServiceClient {
    /**
     * The actions service end point
     */
    private contentActionsServiceEndpoint = "Graph/Actions";

    /**
     * The content preview endpoint
     */
    private contentPreviewApiEndpoint = "view/v1/Preview";

    /**
     * The template url for article images
     */
    private articleImageUrl = "https://img-s-msn-com.akamaized.net/tenant/amp/entityid/${imageId}.img?h=100&w=100&m=6&q=60&u=t&o=t&l=f&f=png";

    /**
     * The template url for provider logo images
     */
    private providerLogoUrl = "https://img-s-msn-com.akamaized.net/tenant/amp/entityid/${logoId}.img?h=16&w=16&m=6&q=60&u=t&o=t&l=f&f=png";

    /**
     * the ocid for request
     */
    private ocid = "feeds";

    /**
     * Initializes a new instance of the `ContentActionsToolbarServiceClient` class.
     * @constructor
     * @public
     * @param {any} fetchImpl - The fetch implementation
     */
    public constructor(private fetchImpl: any) {
    }

    /**
     * Get content preference initial state
     * @param {string} targetId target id
     */
    public async GetPreferenceInitialState(targetId: string): Promise<Status> {
        let initialState: Status = undefined;
        try {
            const response = await this.GetContentStatusRequest(targetId);
            const isLike = response && response["value"] && response["value"].find(obj => obj.actionType === "Like");
            const isDislike = response && response["value"] && response["value"].find(obj => obj.actionType === "Dislike");
            if (isLike) {
                initialState = Status.Like;
            } else if (isDislike) {
                initialState = Status.DisLike;
            } else {
                initialState = Status.None;
            }
        } catch (error) {
            Telemetry.sendAppErrorEvent({
                ...ContentActionsToolbarAppErrors.ContentActionsGetContentStatusActionError,
                message: "Failed obtaining content status",
                pb: {
                    ...ContentActionsToolbarAppErrors.ContentActionsGetContentStatusActionError.pb,
                    customMessage: `Error: ${error}`
                }
            });
        }
        return initialState;
    }

    /**
     * Get required document data
     * @param {string} targetId target id
     */
    public async GetDocumentData(targetId: string): Promise<DocumentData> {
        let documentData: DocumentData = undefined;
        try {
            const contentPreviewData = await this.GetContentPreviewData(targetId);
            documentData = this.TranslateContentPreviewDocumentData(contentPreviewData);
            if (!documentData) {
                throw new Error(`Failed to map content preview data to DocumentData. TargetId: ${targetId}`);
            }
        } catch (error) {
            Telemetry.sendAppErrorEvent({
                ...ContentActionsToolbarAppErrors.ContentActionsGetDocumentDataError,
                message: "Failed obtaining document data",
                pb: {
                    ...ContentActionsToolbarAppErrors.ContentActionsGetDocumentDataError.pb,
                    customMessage: `Error: ${error}`
                }
            });
        }
        return documentData;
    }

    /**
     * The dislike target service call
     * @param targetId target id to be disliked
     */
    public async Dislike(targetId: string): Promise<Response> {
        return this.PostContentActionRequest(targetId, "Dislike");
    }

    /**
     * The undislike target service call
     * @param targetId target id to be undislike
     */
    public async Undislike(targetId: string): Promise<Response> {
        return this.DeleteContentActionRequest(targetId, "Dislike");
    }

    /**
     * The like target service call
     * @param targetId target id to be like
     */
    public async Like(targetId: string): Promise<Response> {
        return this.PostContentActionRequest(targetId, "Like");
    }

    /**
     * The like target service call
     * @param targetId target id to be like
     */
    public async Unlike(targetId: string): Promise<Response> {
        return this.DeleteContentActionRequest(targetId, "Like");
    }

    /**
     * Set request to service to get the metadata about content status
     * @param targetId target id
     */
    private async GetContentStatusRequest(targetId: string): Promise<any> {
        const requestParams = await OneServiceUtility.getBaseRequestData("GET");
        requestParams.credentials = headData.UserIsSignedIn ? "include" : undefined;

        const serviceUrl = getServiceUrlForSubpath(this.contentActionsServiceEndpoint);
        const commonParams = OneServiceUtility.getCommonParams(this.ocid);
        [
            ...commonParams,
            {
                key: "$filter",
                value: `targetId eq '${targetId}'`
            }
        ].forEach(pair => pair.value && serviceUrl.searchParams.set(pair.key, pair.value));

        let contentStatusData: any;
        try {
            contentStatusData = await (sendRequest(
                async () => {
                    const response: Response = await this.fetchImpl(serviceUrl.href, requestParams);
                    if (response.status >= 400) {
                        throw new Error(response.statusText);
                    }
                    return response.json();
                },
                "getContentAction"
            ));
        } catch (error) {
            Telemetry.sendAppErrorEvent({
                ...ContentActionsToolbarAppErrors.ContentActionsContentStatusFetchError,
                message: "Failed to fetch from content status data endpoint",
                pb: {
                    ...ContentActionsToolbarAppErrors.ContentActionsContentStatusFetchError.pb,
                    customMessage: `Service URL: ${serviceUrl}, error: ${error}`
                }
            });
        }

        return contentStatusData;
    }

    /**
     * Gets article content data from ContentPreview endpoint
     * @param articleId the article id
     * @returns Primise<any> The article details data
     */
    private async GetContentPreviewData(articleId: string): Promise<any> {
        const requestParams = await OneServiceUtility.getBaseRequestData("GET");
        requestParams.credentials = headData.UserIsSignedIn ? "include" : undefined;

        // Build the url
        const serviceUrl = new URL(`${this.contentPreviewApiEndpoint}/${articleId}${appEnvironment.ContentServiceUrlBase.search}`, appEnvironment.ContentServiceUrlBase);

        let contentPreviewData: any;
        try {
            contentPreviewData = await (sendRequest(
                async () => {
                    const response: Response = await this.fetchImpl(serviceUrl.href, requestParams);
                    if (response.status >= 400) {
                        throw new Error(response.statusText);
                    }
                    return response.json();
                },
                "getContentAction"
            ));
        } catch (error) {
            Telemetry.sendAppErrorEvent({
                ...ContentActionsToolbarAppErrors.ContentActionsContentPreviewFetchError,
                message: "Failed to fetch from content views data endpoint",
                pb: {
                    ...ContentActionsToolbarAppErrors.ContentActionsContentPreviewFetchError.pb,
                    customMessage: `Service URL: ${serviceUrl}, error: ${error}`
                }
            });
        }

        return contentPreviewData;
    }

    /**
     * Delete an action from target
     * @param targetId target to be deleted
     * @param actionType type of the action
     */
    private async DeleteContentActionRequest(targetId: string, actionType: string): Promise<Response> {
        const requestParams = await OneServiceUtility.getBaseRequestData("DELETE");
        requestParams.credentials = headData.UserIsSignedIn ? "include" : undefined;

        const deleteQueryParam: KeyValuePair<string, string> = {
            key: oneServiceQueryStringKeys.filter,
            value: `actionType eq '${actionType}' and targetId eq '${targetId}'`
        };

        const serviceUrl = getServiceUrlForSubpath(this.contentActionsServiceEndpoint);
        const commonParams = OneServiceUtility.getCommonParams(this.ocid);
        [
            deleteQueryParam,
            ...commonParams
        ].forEach((pair) => pair.value && serviceUrl.searchParams.set(pair.key, pair.value));

        let deleteResponse: Response;
        try {
            deleteResponse = await (sendRequest(
                async () => {
                    const response: Response = await this.fetchImpl(serviceUrl.href, requestParams);
                    if (response.status >= 400) {
                        throw new Error(response.statusText);
                    }
                    return response;
                },
                `deleteContentAction${actionType}`
            ));
        } catch (error) {
            Telemetry.sendAppErrorEvent({
                ...ContentActionsToolbarAppErrors.ContentActionsDeleteActionError,
                message: "Error while calling delete action for content actions toolbar",
                pb: {
                    ...ContentActionsToolbarAppErrors.ContentActionsDeleteActionError.pb,
                    customMessage: `URL: ${serviceUrl.href}, error: ${error}`
                }
            });
        }

        return deleteResponse;
    }

    /**
     * create an action from target
     * @param targetId target to be created
     * @param actionType type of the action
     */
    private async PostContentActionRequest(targetId: string, actionType: string, targetType?: string): Promise<Response> {
        const requestParams: RequestInit = {
            method: "POST",
            body: JSON.stringify({
                actionType,
                targetId,
                targetType
            }),
            credentials: headData.UserIsSignedIn ? "include" : undefined,
            headers: await OneServiceUtility.getOneServiceHeaders()
        };

        const serviceUrl = getServiceUrlForSubpath(this.contentActionsServiceEndpoint);
        const commonParams = OneServiceUtility.getCommonParams(this.ocid);
        commonParams.forEach((pair) => {
            pair.value && serviceUrl.searchParams.set(pair.key, pair.value);
        });

        let postResponse: Response;
        try {
            postResponse = await (sendRequest(
                async () => {
                    const response: Response = await this.fetchImpl(serviceUrl.href, requestParams);
                    if (response.status >= 400) {
                        throw new Error(response.statusText);
                    }
                    return response;
                },
                `getContentAction${actionType}`
            ));
        } catch (error) {
            Telemetry.sendAppErrorEvent({
                ...ContentActionsToolbarAppErrors.ContentActionsPostActionError,
                message: "Error while posting action for content actions toolbar",
                pb: {
                    ...ContentActionsToolbarAppErrors.ContentActionsPostActionError.pb,
                    customMessage: `URL: ${serviceUrl.href}, error: ${error}`
                }
            });
        }

        return postResponse;
    }

    /**
     * Schema Translator for content preview data
     * @param {any} articleDataResponse response data from content preview api
     * @returns {DocumentData} translated document data
     */
    private TranslateContentPreviewDocumentData(articleDataResponse: any): DocumentData {
        const articleImageId = get(articleDataResponse, "images[0].id", undefined);
        const providerLogoId = get(articleDataResponse, "provider.logo.id", undefined);

        return {
            articleTitle: get(articleDataResponse, "title", undefined),
            articleImageUrl: articleImageId ? this.articleImageUrl.replace("${imageId}", articleImageId) : undefined,
            providerName: get(articleDataResponse, "provider.name", undefined),
            providerLogoUrl: providerLogoId ? this.providerLogoUrl.replace("${logoId}", providerLogoId) : undefined
        };
    }
}